/**
 * AWARE Facebook Community Post Kit (Jun 2026).
 *
 * Shared by the Partner "Marketing & Referrals" page and the Admin
 * Partner Marketing Kit verification view. `testidPrefix` namespaces
 * the data-testids so both can be driven independently.
 *
 * The approved square image is used exactly as supplied — this card
 * only displays, downloads and copies it.
 */
import { useState } from "react";
import {
  Facebook, Download, Copy, Check, Link2, FileText, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const btn =
  "inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-40";

export const FacebookPostKitCard = ({ kit, testidPrefix = "facebook-post" }) => {
  const [copied, setCopied] = useState("");
  const t = (s) => `${testidPrefix}-${s}`;

  if (!kit) return null;

  if (!kit.eligible) {
    return (
      <section
        data-testid={t("card")}
        className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h2 className="text-base md:text-lg font-semibold text-slate-900">
            {kit.label}
          </h2>
        </div>
        <p
          data-testid={t("unavailable")}
          className="mt-2 text-sm text-slate-600"
        >
          Your tracked AWARE link isn&rsquo;t ready yet. Once your partner
          slug is issued this kit will appear here automatically.
        </p>
      </section>
    );
  }

  const copy = async (key, text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(""), 2200);
    } catch {
      toast.error("Couldn't copy — please select the text and copy manually");
    }
  };

  const CopyBtn = ({ id, text, label, icon: Icon = Copy, primary }) => (
    <button
      type="button"
      data-testid={t(id)}
      onClick={() => copy(id, text, label)}
      className={`${btn} ${
        primary
          ? "bg-cyan-600 text-white hover:bg-cyan-700"
          : "border border-slate-200 text-slate-700 hover:bg-slate-50"
      }`}
    >
      {copied === id ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      {copied === id ? "Copied" : label}
    </button>
  );

  return (
    <section
      data-testid={t("card")}
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-center gap-2">
        <Facebook className="w-4 h-4 text-cyan-600" />
        <h2 className="text-base md:text-lg font-semibold text-slate-900">
          {kit.label}
        </h2>
      </div>
      <p
        data-testid={t("subtext")}
        className="mt-1 text-sm font-medium text-slate-600"
      >
        {kit.subtext}
      </p>
      <p
        data-testid={t("supporting")}
        className="mt-2 text-sm text-slate-600 max-w-2xl leading-relaxed"
      >
        {kit.supporting_text}
      </p>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,260px)_1fr]">
        {/* Approved image */}
        <div>
          <img
            data-testid={t("image")}
            src={kit.image_preview_path || kit.image_download_path}
            alt={kit.alt_text}
            width={kit.image_width}
            height={kit.image_height}
            className="w-full max-w-[260px] rounded-xl border border-slate-200"
          />
          <a
            data-testid={t("download-btn")}
            href={kit.image_download_path}
            download={kit.image_filename}
            className={`${btn} mt-3 w-full max-w-[260px] bg-slate-900 text-white hover:bg-slate-800`}
          >
            <Download className="w-4 h-4" /> Download Image
          </a>
          <p className="mt-1.5 text-xs text-slate-500">
            {kit.image_width} &times; {kit.image_height}px &middot; approved
            artwork, please use as supplied
          </p>
        </div>

        {/* Copy + text */}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5">
            Suggested post
          </div>
          <pre
            data-testid={t("post-text")}
            className="whitespace-pre-wrap break-words rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-sm text-slate-700 leading-relaxed font-sans"
          >
            {kit.post_text_with_link}
          </pre>

          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">
              Your tracked AWARE link
            </div>
            <code
              data-testid={t("tracked-url")}
              className="block break-all rounded-lg bg-slate-900 px-3 py-2 text-xs text-cyan-300"
            >
              {kit.tracked_url}
            </code>
            <p className="mt-1.5 text-xs text-slate-500">
              Tagged{" "}
              <b>
                src={kit.source}
              </b>{" "}
              &middot;{" "}
              <b>cmp={kit.campaign}</b> so bookings from this post are
              credited to you.
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <CopyBtn
              id="copy-post-text-with-link"
              text={kit.post_text_with_link}
              label="Copy Post Text with My Link"
              icon={FileText}
              primary
            />
            <CopyBtn
              id="copy-post-text"
              text={kit.post_text}
              label="Copy Post Text"
            />
            <CopyBtn
              id="copy-link"
              text={kit.tracked_url}
              label="Copy My AWARE Link"
              icon={Link2}
            />
          </div>
        </div>
      </div>

      {/* How to use it */}
      <div
        data-testid={t("how-to-use")}
        className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4"
      >
        <div className="text-sm font-semibold text-slate-900">How to use it</div>
        <ol className="mt-2 space-y-1 text-sm text-slate-600 list-decimal list-inside">
          {(kit.how_to_use || []).map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <p
          data-testid={t("tip")}
          className="mt-3 text-sm font-medium text-cyan-800"
        >
          {kit.tip}
        </p>
      </div>
    </section>
  );
};

export default FacebookPostKitCard;
